import React, { useState } from 'react';
import { ActivityIndicator, Checkbox } from 'react-native-paper';
import { green, mint, red } from '@/shared/styles/colors';
import { BodyText } from '@/shared/components/StyledText';
import styled from '@emotion/native';
import { Linking, TouchableOpacity } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useLazyQuery, useQuery } from '@apollo/client';
import { ONBOARDING_FORM_HEIGHT } from '@/shared/styles/constants';
import {
  GET_STRIPE_ACCOUNT_IS_ACTIVE,
  GET_STRIPE_ACCOUNT_LINK,
} from '@/shared/queries';
import { QueryResult } from '@/shared/types/generated';
import useBreakpoint from '@/shared/hooks/useBreakpoint';
import EmailLink from '../components/EmailLink';

interface StylistAddPaymentProps {
  isCheckboxChecked: boolean;
  setIsCheckboxChecked: (checked: boolean) => void;
  setIsLoading: (loading: boolean) => void;
}

const StylistAddPayment: React.FC<StylistAddPaymentProps> = ({
  isCheckboxChecked,
  setIsCheckboxChecked,
  setIsLoading,
}) => {
  const { isDesktop } = useBreakpoint();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { data, loading } = useQuery<QueryResult<'stripeAccountLink'>>(
    GET_STRIPE_ACCOUNT_LINK,
  );

  const errorCopy =
    "We couldn't find your Stripe account. Make sure you complete Stripe setup at the link above using the same email, name, and phone number as your EMME account.";

  const [
    checkIfStripeAccountIsActive,
    { loading: stripeAccountIsActiveLoading },
  ] = useLazyQuery<QueryResult<'stripeAccountIsActive'>>(
    GET_STRIPE_ACCOUNT_IS_ACTIVE,
    {
      onCompleted: (completedData) => {
        if (completedData.stripeAccountIsActive === true) {
          setIsLoading(false);
          setErrorMessage(null);
          setIsCheckboxChecked(true);
        } else {
          setIsLoading(false);
          setErrorMessage(errorCopy);
        }
      },
      onError: () => {
        setIsLoading(false);
        setErrorMessage(
          'There was an error checking your Stripe account. Please try again later.',
        );
      },
      fetchPolicy: 'no-cache',
    },
  );

  const handleStripeLinkPress = async () => {
    if (data?.stripeAccountLink) {
      await Linking.openURL(data.stripeAccountLink);
    }
  };

  const onCheckBoxPress = () => {
    if (!isCheckboxChecked) {
      checkIfStripeAccountIsActive();
    } else {
      setIsCheckboxChecked(!isCheckboxChecked);
    }
  };

  if (loading) {
    return (
      <Container
        style={{
          height: ONBOARDING_FORM_HEIGHT,
          justifyContent: isDesktop ? 'center' : 'flex-start',
        }}
      >
        <ActivityIndicator size="large" color={green} />
      </Container>
    );
  }

  return (
    <Container>
      <InstructionsTitle>Setup Stripe Account*:</InstructionsTitle>
      <StepsContainer>
        <Step>
          <StepText isDesktop={isDesktop}>
            <StepNumber isDesktop={isDesktop}>Step 1:</StepNumber>
            Go to{' '}
            <TouchableOpacity
              onPress={handleStripeLinkPress}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <LinkText
                style={{
                  marginRight: 4,
                  lineHeight: 20,
                  marginBottom: isDesktop ? 0 : -4,
                }}
              >
                Stripe&apos;s website
              </LinkText>
              <Feather
                style={{ marginBottom: isDesktop ? 0 : -4 }}
                name="external-link"
                size={14}
                color={green}
              />
            </TouchableOpacity>{' '}
            to create your account.
          </StepText>
        </Step>

        <Step>
          <StepText isDesktop={isDesktop}>
            <StepNumber isDesktop={isDesktop}>Step 2:</StepNumber>
            Follow Stripe&apos;s instructions to set up your payout details.
            Make sure you use the same email, name, and phone number you are
            using to sign up with EMME.
          </StepText>
        </Step>

        <Step>
          <StepText isDesktop={isDesktop}>
            <StepNumber isDesktop={isDesktop}>Step 3:</StepNumber>
            Once completed, return to this page to create account
          </StepText>
        </Step>
      </StepsContainer>

      <Spacer />

      <CheckboxContainer onPress={onCheckBoxPress}>
        {stripeAccountIsActiveLoading ? (
          <LoadingWrapper>
            <ActivityIndicator size="small" color={green} />
          </LoadingWrapper>
        ) : (
          <CheckboxWrapper>
            <Checkbox.Android
              status={isCheckboxChecked ? 'checked' : 'unchecked'}
              onPress={onCheckBoxPress}
              color={green}
            />
          </CheckboxWrapper>
        )}
        <CheckboxLabel>
          I&apos;ve successfully created my Stripe account to receive payments.
        </CheckboxLabel>
      </CheckboxContainer>
      <ErrorView>
        {errorMessage && (
          <ErrorText>
            {errorMessage}
            {'\n'}
            <Row>
              <HelpText>Need help?</HelpText>
              <EmailLink
                style={{
                  fontSize: 11,
                  textDecorationLine: 'underline',
                  fontWeight: 'bold',
                  paddingLeft: 4,
                }}
              >
                Contact Support
              </EmailLink>
            </Row>
          </ErrorText>
        )}
      </ErrorView>
    </Container>
  );
};

const Container = styled.View({
  flex: 1,
  padding: 31,
  paddingTop: 27,
  paddingBottom: 10,
});

const InstructionsTitle = styled(BodyText)({
  marginBottom: 22,
  fontSize: 14,
});

const StepsContainer = styled.View({
  marginBottom: 24,
});

const Step = styled.View({
  marginBottom: 24,
});

const StepNumber = styled(BodyText)<{ isDesktop: boolean }>(
  ({ isDesktop }) => ({
    fontWeight: '600',
    marginRight: 4,
    fontSize: isDesktop ? 18 : 16,
  }),
);

const StepText = styled(BodyText)<{ isDesktop: boolean }>(({ isDesktop }) => ({
  flex: 1,
  fontSize: isDesktop ? 18 : 16,
}));

const CheckboxContainer = styled.TouchableOpacity({
  flexDirection: 'row',
  alignItems: 'center',
  padding: 16,
  paddingTop: 13,
  backgroundColor: mint,
});

const LoadingWrapper = styled.View({
  width: 48,
  height: 24,
  justifyContent: 'center',
  alignItems: 'center',
});

const CheckboxWrapper = styled.View({
  marginRight: 12,
});

const CheckboxLabel = styled(BodyText)({
  marginLeft: 12,
  flex: 1,
  fontWeight: 'bold',
  fontSize: 15,
  lineHeight: 20,
  color: green,
});

const Spacer = styled.View({
  flex: 1,
});

const LinkText = styled(BodyText)({
  color: green,
  textDecorationLine: 'underline',
  fontWeight: 'bold',
});

const ErrorView = styled.View({
  paddingVertical: 15,
  width: 'auto',
});

const ErrorText = styled(BodyText)({
  color: red,
  fontSize: 11,
  lineHeight: 14,
  fontWeight: 'bold',
});

const Row = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
});

const HelpText = styled(BodyText)({
  fontSize: 11,
  lineHeight: 14,
  color: green,
});

export default StylistAddPayment;
